import React, { useEffect, useRef, useState } from 'react';
import { Eye, EyeOff, Plus, Trash2, Search, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Small inline "Find my business" picker. Types a query, waits 350ms
// then hits /api/shops/lookup-place. Clicking a hit stamps the shop's
// google_place_id + auto-flips reviews_source to 'google'. Saves the
// admin from having to copy Place IDs out of Google Maps URLs.
const PlaceFinder = ({ form, setForm }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);   // null = idle, [] = no hits
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const timer = useRef(null);

  const search = async (q) => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/shops/lookup-place?q=${encodeURIComponent(q)}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || `Search failed (${res.status})`);
      setResults(body.results || []);
    } catch (e) {
      setError(e.message);
      setResults([]);
    } finally {
      setBusy(false);
    }
  };

  const onChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (timer.current) clearTimeout(timer.current);
    if (v.trim().length < 3) { setResults(null); return; }
    timer.current = setTimeout(() => search(v.trim()), 350);
  };

  const pick = (r) => {
    setForm(prev => ({
      ...prev,
      google_place_id: r.place_id,
      reviews_source: 'google',
    }));
    setQuery(r.display_name);
    setResults(null);
  };

  return (
    <div style={{ position: 'relative', marginBottom: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Search size={14} style={{ color: 'var(--mute)', flex: '0 0 auto' }} />
        <input
          className="inp"
          style={{ flex: 1 }}
          placeholder="Find your business — e.g. Sharp & Sons Oldham"
          value={query}
          onChange={onChange}
          data-testid="reviews-place-search"
        />
      </div>
      {busy && (
        <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--mute)' }}>Searching…</div>
      )}
      {error && (
        <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--danger, #c33)' }}>{error}</div>
      )}
      {results !== null && results.length === 0 && !busy && !error && (
        <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--mute)' }}>
          No matches — try adding your city or postcode.
        </div>
      )}
      {results !== null && results.length > 0 && (
        <div
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5,
            marginTop: 4, background: 'var(--card, #fff)',
            border: '1px solid var(--line)', borderRadius: 8,
            boxShadow: '0 8px 20px -8px rgba(0,0,0,0.2)',
            maxHeight: 260, overflowY: 'auto',
          }}
          data-testid="reviews-place-results"
        >
          {results.map((r, i) => (
            <button
              key={r.place_id || i}
              type="button"
              onClick={() => pick(r)}
              data-testid={`reviews-place-result-${i}`}
              style={{
                display: 'flex', width: '100%', textAlign: 'left',
                padding: '10px 12px', border: 'none', background: 'transparent',
                borderBottom: i === results.length - 1 ? 'none' : '1px solid var(--line)',
                cursor: 'pointer', gap: 10, alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{r.display_name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--mute)', marginTop: 2 }}>{r.formatted_address}</div>
              </div>
              {r.rating != null && (
                <div style={{ fontSize: 11, color: 'var(--mute)', whiteSpace: 'nowrap' }}>
                  ⭐ {r.rating} · {r.total_ratings}
                </div>
              )}
              {form.google_place_id === r.place_id && (
                <Check size={16} style={{ color: 'var(--acc)', flex: '0 0 auto', marginTop: 2 }} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Reviews panel — Google (read-only proxy) OR Instacut list.
// TODO: post-booking review email/SMS trigger to be built separately
// once the booking-completed hook lands.
export const ReviewsPanel = ({
  form, setForm,
  googlePreview, googleTesting, testGoogleReviews,
  updateReview, addReview, deleteReview, toggleReviewHidden,
}) => (
  <div className="panel" style={{ padding: 18, marginBottom: 14 }} data-testid="reviews-panel">
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <label style={{ fontSize: 12.5, fontWeight: 600 }}>Reviews</label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
        <input
          type="checkbox"
          checked={form.reviews_source === 'google'}
          onChange={(e) => setForm(prev => ({ ...prev, reviews_source: e.target.checked ? 'google' : 'instacut' }))}
          data-testid="reviews-google-toggle"
        />
        Enable Google reviews
      </label>
    </div>

    {form.reviews_source === 'google' ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <PlaceFinder form={form} setForm={setForm} />
        <input
          className="inp"
          placeholder="Google Place ID (auto-filled from Find your business above)"
          value={form.google_place_id}
          onChange={(e) => setForm(prev => ({ ...prev, google_place_id: e.target.value }))}
          data-testid="reviews-google-place-id"
        />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            className="btn"
            onClick={testGoogleReviews}
            disabled={googleTesting || !(form.google_place_id || '').trim()}
            data-testid="reviews-google-test"
          >
            {googleTesting ? 'Testing…' : 'Test'}
          </button>
          {googlePreview && !googlePreview.error && (
            <span style={{ fontSize: 12.5, color: 'var(--mute)' }} data-testid="reviews-google-preview">
              ⭐ {googlePreview.rating ?? '—'} · {googlePreview.total ?? 0} reviews
            </span>
          )}
          {googlePreview?.error && (
            <span style={{ fontSize: 12, color: 'var(--danger, #c33)' }} data-testid="reviews-google-error">
              {googlePreview.error}
            </span>
          )}
        </div>
        <small style={{ color: 'var(--mute)', fontSize: 12 }}>
          Only the 3 most-recent reviews will show on your landing page. The <b>All reviews</b> button links to Google.
        </small>
      </div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {form.reviews.length === 0 && (
          <div style={{ fontSize: 12.5, color: 'var(--mute)' }}>No reviews yet — add one below.</div>
        )}
        {form.reviews.map((r, i) => (
          <div
            key={r.id}
            data-testid={`review-edit-${i}`}
            style={{
              border: '1px solid var(--line)', borderRadius: 8, padding: 10,
              opacity: r.hidden ? 0.55 : 1,
            }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <input
                className="inp"
                style={{ flex: 1 }}
                placeholder="Reviewer name"
                value={r.name}
                onChange={(e) => updateReview(i, { name: e.target.value })}
                data-testid={`review-name-${i}`}
              />
              <select
                className="inp"
                style={{ width: 80 }}
                value={r.rating}
                onChange={(e) => updateReview(i, { rating: Number(e.target.value) })}
                data-testid={`review-rating-${i}`}
                aria-label="Star rating"
              >
                {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{'★'.repeat(n)}</option>)}
              </select>
              <button
                type="button"
                className="icon-btn"
                title={r.hidden ? 'Show on landing page' : 'Hide from landing page'}
                onClick={() => toggleReviewHidden(i)}
                data-testid={`review-hide-${i}`}
              >
                {r.hidden ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <button
                type="button"
                className="icon-btn"
                title="Delete review"
                onClick={() => deleteReview(i)}
                data-testid={`review-delete-${i}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
            <textarea
              className="inp"
              rows="2"
              placeholder="What did they say?"
              value={r.review}
              onChange={(e) => updateReview(i, { review: e.target.value })}
              data-testid={`review-text-${i}`}
            />
          </div>
        ))}
        <button
          type="button"
          className="btn"
          onClick={addReview}
          style={{ alignSelf: 'flex-start' }}
          data-testid="review-add"
        >
          <Plus size={14} /> Add review
        </button>
      </div>
    )}
  </div>
);
